import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Course from "./pages/Course";
import Section from "./pages/Section";
import QuizArena from "./pages/QuizArena";

import BitcoinSimulator from "./pages/BitcoinSimulator";
import Pricing from "./pages/Subscription";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import Profile from "./pages/Profile";
import Mission from "./pages/Mission";
import Assistant from "./pages/Assistant";
import Rewards from "./pages/Rewards";
import Achievements from "./pages/Achievements";
import XPStore from "./pages/XPStore";
import KidMode from "./pages/KidMode";
import Leaderboard from "./pages/Leaderboard";
import BrainTrust from "./pages/BrainTrust";
import Timeline from "./pages/Timeline";
import WeeklyBrief from "./pages/WeeklyBrief";
import RabbitHole from "./pages/RabbitHole";
import PriceAlerts from "./pages/PriceAlerts";
import EasterEggs from "./pages/EasterEggs";
import AssembledStory from "./pages/AssembledStory";
import MythBuster from "./pages/MythBuster";
import DemoSection from "./pages/DemoSection";
import JourneyTracker from "./pages/JourneyTracker";
import EducatorDashboard from "./pages/EducatorDashboard";
import VerifyEmail from "./pages/VerifyEmail";
import Referral from "./pages/Referral";
import FundFamilyApply from "./pages/FundFamilyApply";
import AdminFundFamily from "./pages/AdminFundFamily";
import AdminDashboard from "./pages/AdminDashboard";
import Certificate from "./pages/Certificate";
import FamilyProgressReport from "./pages/FamilyProgressReport";
import Legal from "./pages/Legal";
import TermsOfService from "./pages/legal/TermsOfService";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import FinancialDisclaimer from "./pages/legal/FinancialDisclaimer";
import FundFamilyTerms from "./pages/legal/FundFamilyTerms";
import CoppaNotice from "./pages/legal/CoppaNotice";

// Context
const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios instance
const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" }
});

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only force logout if not already on login/register/public pages
      const publicPaths = ["/login", "/register", "/demo", "/verify-email", "/"];
      const currentPath = window.location.pathname;
      if (!publicPaths.includes(currentPath)) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export { api, API, BACKEND_URL };

// Auth Provider
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    
    if (token && savedUser) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 > Date.now()) {
          setUser(JSON.parse(savedUser));
        } else {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      } catch {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (email, password, firstName) => {
    const response = await api.post("/auth/register", { 
      email, 
      password, 
      first_name: firstName 
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get("/auth/me");
      localStorage.setItem("user", JSON.stringify(response.data));
      setUser(response.data);
      return response.data;
    } catch {
      logout();
    }
  }, [logout]);

  const updateUser = useCallback((updates) => {
    const updated = { ...user, ...updates };
    localStorage.setItem("user", JSON.stringify(updated));
    setUser(updated);
  }, [user]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      refreshUser, 
      updateUser,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Protected Route
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to onboarding if not complete
  if (!user.onboarding_complete && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}

// Page Transition Wrapper
function PageTransition({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
}

// Main App
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="min-h-screen bg-[#050505]">
          <Toaster 
            theme="dark" 
            position="top-right"
            toastOptions={{
              style: {
                background: '#161616',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff'
              }
            }}
          />
          <AnimatePresence mode="wait">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<PageTransition><Landing /></PageTransition>} />
              <Route path="/login" element={<PageTransition><Login /></PageTransition>} />
              <Route path="/register" element={<PageTransition><Register /></PageTransition>} />
              <Route path="/verify-email" element={<PageTransition><VerifyEmail /></PageTransition>} />

              {/* Protected Routes */}
              <Route path="/onboarding" element={
                <ProtectedRoute>
                  <PageTransition><Onboarding /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <PageTransition><Dashboard /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/course" element={
                <ProtectedRoute>
                  <PageTransition><Course /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/course/section/:sectionId" element={
                <ProtectedRoute>
                  <PageTransition><Section /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/games/quiz-arena" element={
                <ProtectedRoute>
                  <PageTransition><QuizArena /></PageTransition>
                </ProtectedRoute>
              } />
              {/* Climbing game removed */}
              <Route path="/games/simulator" element={
                <ProtectedRoute>
                  <PageTransition><BitcoinSimulator /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <PageTransition><Profile /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/assistant" element={
                <ProtectedRoute>
                  <PageTransition><Assistant /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/rewards" element={
                <ProtectedRoute>
                  <PageTransition><Rewards /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/achievements" element={
                <ProtectedRoute>
                  <PageTransition><Achievements /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/xp-store" element={
                <ProtectedRoute>
                  <PageTransition><XPStore /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/kid-mode" element={
                <ProtectedRoute>
                  <PageTransition><KidMode /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/leaderboard" element={
                <ProtectedRoute>
                  <PageTransition><Leaderboard /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/brain-trust" element={
                <ProtectedRoute>
                  <PageTransition><BrainTrust /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/timeline" element={
                <ProtectedRoute>
                  <PageTransition><Timeline /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/weekly-brief" element={
                <ProtectedRoute>
                  <PageTransition><WeeklyBrief /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/rabbit-hole" element={
                <ProtectedRoute>
                  <PageTransition><RabbitHole /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/price-alerts" element={
                <ProtectedRoute>
                  <PageTransition><PriceAlerts /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/easter-eggs" element={
                <ProtectedRoute>
                  <PageTransition><EasterEggs /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/easter-eggs/story" element={
                <ProtectedRoute>
                  <PageTransition><AssembledStory /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/journey" element={
                <ProtectedRoute>
                  <PageTransition><JourneyTracker /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/educator" element={
                <ProtectedRoute>
                  <PageTransition><EducatorDashboard /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/referral" element={
                <ProtectedRoute>
                  <PageTransition><Referral /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/admin/fund-a-family" element={
                <ProtectedRoute>
                  <PageTransition><AdminFundFamily /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <PageTransition><AdminDashboard /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/educator/certificate/:studentId" element={
                <ProtectedRoute>
                  <PageTransition><Certificate /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/fund-a-family" element={
                <PageTransition><FundFamilyApply /></PageTransition>
              } />
              <Route path="/legal" element={<PageTransition><Legal /></PageTransition>} />
              <Route path="/legal/terms" element={<PageTransition><TermsOfService /></PageTransition>} />
              <Route path="/legal/privacy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
              <Route path="/legal/disclaimer" element={<PageTransition><FinancialDisclaimer /></PageTransition>} />
              <Route path="/legal/fund-a-family" element={<PageTransition><FundFamilyTerms /></PageTransition>} />
              <Route path="/legal/coppa" element={<PageTransition><CoppaNotice /></PageTransition>} />
              <Route path="/educator/progress-report" element={
                <ProtectedRoute>
                  <PageTransition><FamilyProgressReport /></PageTransition>
                </ProtectedRoute>
              } />
              <Route path="/myths" element={
                <PageTransition><MythBuster /></PageTransition>
              } />
              <Route path="/mission" element={
                <PageTransition><Mission /></PageTransition>
              } />
              <Route path="/demo" element={
                <PageTransition><DemoSection /></PageTransition>
              } />
              <Route path="/pricing" element={
                <PageTransition><Pricing /></PageTransition>
              } />
              <Route path="/purchase/success" element={
                <ProtectedRoute>
                  <PageTransition><SubscriptionSuccess /></PageTransition>
                </ProtectedRoute>
              } />
              {/* Legacy redirect */}
              <Route path="/subscription" element={<Navigate to="/pricing" replace />} />
              <Route path="/subscription/success" element={<Navigate to="/purchase/success" replace />} />

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AnimatePresence>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
